/**
 * Builds small, zoomed-in images of the dropped/garbled glyph(s) inside a text
 * selection, cropped from the rendered page canvas. Shown in the sanitization
 * dialog next to each "unrecognized character" row, so a reviewer can see what
 * a control/PUA character actually looked like in the source (the text layer
 * renders it invisibly).
 *
 * `captureGlyphSnippets` returns one entry per dropped-glyph character, in
 * reading order, aligned with the "unidentified" rows the sanitizer produces
 * (findReplacements walks the same characters in the same order). Entries are
 * `null` where a crop couldn't be made, so the row falls back to a "?" badge
 * and alignment is preserved.
 *
 * Shared by the SDEv1 (WPD) and SDEv2 text-select paths; both pass a DOM Range,
 * the page <canvas>, and the document that owns them. Realm-safe (WPD lives in
 * an iframe): no cross-realm `instanceof`, and sub-ranges/output canvases are
 * created from the passed-in document.
 */

const ZOOM = 3; // magnify the crop so a small glyph is legible
const CONTEXT_PX = 3; // CSS px of surrounding context kept on each side

/**
 * Characters the sanitizer surfaces as "unidentified" (mirrors
 * textSanitization): low control, C1/DEL, PUA, and the replacement char.
 * Excludes genuine non-characters (FFFE/FFFF), which are removed, not editable.
 */
function isDroppedGlyphCp(cp) {
	return (
		cp <= 0x08 ||
		(cp >= 0x0e && cp <= 0x1f) ||
		cp === 0x7f ||
		(cp >= 0x80 && cp <= 0x9f) ||
		cp === 0xfffd ||
		(cp >= 0xe000 && cp <= 0xf8ff) ||
		(cp >= 0xf0000 && cp <= 0xffffd) ||
		(cp >= 0x100000 && cp <= 0x10fffd)
	);
}

/**
 * Left edge of the character that follows position `i` in `node`, in reading
 * order - i.e. the dropped glyph's right edge. Looks in the same text node
 * first, then in following text nodes (pdf.js often puts the dropped glyph in
 * its own span). Only accepts a same-line neighbour just to the right; returns
 * null when there is none (end of line), so the caller uses a safe ~1 em box.
 */
function nextGlyphLeft(doc, root, node, i, ref) {
	const sane = (b) =>
		b &&
		b.height > 0 &&
		b.left > ref.left &&
		b.left < ref.left + ref.height * 2 &&
		Math.abs(b.top - ref.top) < ref.height * 0.6;
	const leftOf = (n, off) => {
		try {
			const r = doc.createRange();
			r.setStart(n, off);
			r.setEnd(n, off + 1);
			return r.getBoundingClientRect();
		} catch {
			return null;
		}
	};
	if (i + 1 < node.data.length) {
		const b = leftOf(node, i + 1);
		if (sane(b)) return b.left;
	}
	const tw = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	tw.currentNode = node;
	let n = tw.nextNode();
	let hops = 0;
	while (n && hops++ < 5) {
		if (n.data && n.data.length) {
			const b = leftOf(n, 0);
			if (sane(b)) return b.left;
		}
		n = tw.nextNode();
	}
	return null;
}

/**
 * Client rect (or null) for each dropped-glyph character in the range, in
 * reading order. Applies the same low-control-between-digits exclusion as the
 * sanitizer (those become a decimal point, not an unidentified row) so the
 * sequence lines up with the dialog's unidentified rows.
 */
function droppedGlyphRects(range) {
	const out = [];
	const root = range.commonAncestorContainer;
	const doc = root.ownerDocument || document;
	const start = root.nodeType === Node.TEXT_NODE ? root.parentNode : root;
	if (!start) return out;
	const walker = doc.createTreeWalker(start, NodeFilter.SHOW_TEXT);
	let node;
	while ((node = walker.nextNode())) {
		if (!range.intersectsNode(node)) continue;
		const text = node.data;
		const from = node === range.startContainer ? range.startOffset : 0;
		const to = node === range.endContainer ? range.endOffset : text.length;
		for (let i = from; i < to; i++) {
			const cp = text.charCodeAt(i);
			if (!isDroppedGlyphCp(cp)) continue;
			const isLowControl = cp <= 0x08 || (cp >= 0x0e && cp <= 0x1f);
			if (
				isLowControl &&
				/\d/.test(text[i - 1] || "") &&
				/\d/.test(text[i + 1] || "")
			) {
				continue; // decimal point, handled as a separate row type
			}
			let rect = null;
			try {
				const r = doc.createRange();
				r.setStart(node, i);
				r.setEnd(node, i + 1);
				const box = r.getBoundingClientRect();
				if (box.height > 0) {
					// A dropped glyph reports zero width (drawn invisibly). Its
					// right edge is exactly where the next character starts - found
					// even when that char is in a later span. When there's no
					// same-line neighbour (end of line), nothing sits to the right,
					// so a ~1 em box is safe.
					const ref = { left: box.left, top: box.top, height: box.height };
					const nl = nextGlyphLeft(doc, start, node, i, ref);
					const right = nl != null ? nl : box.left + box.height;
					rect = {
						left: box.left,
						top: box.top,
						bottom: box.bottom,
						height: box.height,
						width: Math.max(box.width, right - box.left),
					};
				}
			} catch {
				rect = null;
			}
			out.push(rect);
		}
	}
	return out;
}

/**
 * Canvas-px crop box bounding a single glyph. `rect.width` is the glyph's
 * advance (derived from the next character's position), so the crop is tight
 * around just this character - a hair of context keeps edges from clipping.
 */
function cropBox(rect, box, sx, sy, canvas) {
	const glyphW = rect.width;
	const x = Math.max(0, (rect.left - CONTEXT_PX - box.left) * sx);
	const y = Math.max(0, (rect.top - CONTEXT_PX - box.top) * sy);
	const w = Math.min(canvas.width - x, (glyphW + CONTEXT_PX * 2) * sx);
	const h = Math.min(canvas.height - y, (rect.height + CONTEXT_PX * 2) * sy);
	if (w <= 0 || h <= 0) return null;
	return { x, y, w, h };
}

/**
 * @param {Range} range - the text selection.
 * @param {HTMLCanvasElement} canvas - the rendered page canvas (any realm).
 * @param {Document} targetDoc - document to create the output canvas in.
 * @returns {Array<string|null>} one zoomed PNG data URL per dropped-glyph
 *   character (reading order), or null where no crop could be produced.
 */
export function captureGlyphSnippets(range, canvas, targetDoc) {
	try {
		if (
			!range ||
			!canvas ||
			!canvas.width ||
			typeof canvas.getContext !== "function"
		) {
			return [];
		}
		const rects = droppedGlyphRects(range);
		if (!rects.length) return [];

		const box = canvas.getBoundingClientRect();
		const sx = canvas.width / box.width; // device px per CSS px
		const sy = canvas.height / box.height;
		const doc = targetDoc || document;

		return rects.map((rect) => {
			if (!rect) return null;
			const b = cropBox(rect, box, sx, sy, canvas);
			if (!b) return null;
			const out = doc.createElement("canvas");
			out.width = Math.round(b.w * ZOOM);
			out.height = Math.round(b.h * ZOOM);
			const ctx = out.getContext("2d");
			ctx.imageSmoothingEnabled = true;
			ctx.imageSmoothingQuality = "high";
			ctx.fillStyle = "#fff";
			ctx.fillRect(0, 0, out.width, out.height);
			ctx.drawImage(canvas, b.x, b.y, b.w, b.h, 0, 0, out.width, out.height);
			return out.toDataURL("image/png");
		});
	} catch {
		return [];
	}
}
